/**
 * Projektdetail — Puls ist die Startseite (Paket 12).
 */

import { useMemo, useState } from "react";
import { useParams, useSearch } from "@tanstack/react-router";
import { Leerzustand } from "@/components/Leerzustand";
import { Button } from "@/components/Button";
import { Badge } from "@/components/Badge";
import { useIch, useProjekt, useProjektVerzeichnis } from "@/lib/haken";
import { darfAnlegen } from "@/lib/rollen";
import {
  type Akteursart,
  type Objektart,
  type Rolle,
  type Vorgang,
} from "@cubic/shared";
import { AufgabenListe } from "@/seiten/aufgaben/AufgabenListe";
import { EntwurfOffen } from "@/components/entwurf/EntwurfOffen";
import { UebersichtReiter } from "@/seiten/projekt/UebersichtReiter";
import { PulsReiter } from "@/seiten/projekt/PulsReiter";
import { MeilensteineReiter } from "@/seiten/projekt/MeilensteineReiter";
import { LieferungReiter } from "@/seiten/projekt/LieferungReiter";
import { DurchsichtReiter } from "@/seiten/projekt/DurchsichtReiter";
import { EinstellungenReiter } from "@/seiten/projekt/EinstellungenReiter";

const REITER = [
  "uebersicht",
  "puls",
  "aufgaben",
  "meilensteine",
  "lieferung",
  "durchsicht",
  "regeln",
  "klaerungen",
  "seiten",
  "personen",
  "einstellungen",
] as const;
type Reiter = (typeof REITER)[number];

/*
 * Die Beschriftungen der Reiter sind mit der Reiterleiste entfallen. Sie stehen
 * jetzt an zwei Stellen, an denen sie gebraucht werden: in der Schiene als
 * Punkte und in `lib/brotkrume.ts` für den Pfad in der Kopfleiste. `REITER`
 * selbst bleibt — es trägt den Typ und die erlaubten Werte des Suchparameters.
 */

/**
 * Eine Aufgabenkarte am Zeitstrahl — mit dem MeilensteineReiter entfallen.
 * Der neue Reiter zeigt den Plan als Zeitband mit Bahnen, nicht mehr als
 * Strahl mit Karten.
 */

export function ProjektDetail() {
  const { id } = useParams({ from: "/_app/projekte/$id" });
  const search = useSearch({ from: "/_app/projekte/$id" });
  const reiter: Reiter = REITER.includes(search.reiter as Reiter)
    ? (search.reiter as Reiter)
    : "uebersicht";

  const projekt = useProjekt(id);
  const ich = useIch();
  const verzeichnis = useProjektVerzeichnis(id);
  /*
   * Klärungen, Befunde und Merge Requests wurden hier nur für die entfallene
   * Kennzahlenreihe geladen — drei Abfragen bei jedem Aufruf des
   * Projektdetails, für fünf Zahlen, die nirgends mehr stehen. Die Reiter
   * holen sich, was sie brauchen, selbst.
   */
  const rolle = useMemo(() => {
    return ich.data?.projekte.find((p) => p.projektId === id)?.rolle as
      | Rolle
      | undefined;
  }, [ich.data?.projekte, id]);

  const kannAnlegen = darfAnlegen(rolle);
  const [neuOffen, setzeNeu] = useState(false);

  if (projekt.isLoading) {
    return <p className="text-sm text-gray-500">Lade Projekt …</p>;
  }
  if (projekt.isError || !projekt.data) {
    return (
      <Leerzustand
        titel="Diesen Eintrag gibt es nicht."
        hinweis="Das Projekt ist nicht sichtbar oder existiert nicht."
      />
    );
  }

  const p = projekt.data;

  return (
    <>
      {/*
        Hier stand der alte Projektkopf: Rücksprung, farbiger Punkt, Name in
        22px fett, Beschreibung, Kürzel und ein Rollen-Badge — alles noch in der
        Grauskala vor der Umstellung. Er ist entfallen, weil er dem Reiter einen
        **zweiten** Titel voranstellte: Gemessen lag darüber ein `<h1>` in 22px,
        während der Reiter darunter seinen eigenen in 46 oder 54px führte. Damit
        saß auf keiner Projektseite die Überschrift dort, wo sie auf allen
        anderen sitzt.

        Verloren geht dabei nichts:
        - Der Name steht im Titel des Übersichtsreiters.
        - Kürzel und Kontext stehen in der Kontextzeile darüber und in der
          Brotkrume der Kopfleiste.
        - Der Rücksprung ist der Punkt „Projekte" in der Schiene.
        - Die Rolle nennt der Fuß der Schiene unter dem Namen der Person.
      */}

      {/*
        Hier stand die Kennzahlenreihe: fünf gerundete weiße Kacheln mit
        Schatten. Sie ist entfallen — der Entwurf kennt sie nicht. Er führt
        Zahlen als Zahlenband über einer Tintenlinie, nicht als Kartenraster,
        und jeder der fünf Werte steht ohnehin schon dort, wo er hingehört:
        die offenen Aufgaben im Board, die Klärungen im Klärungsreiter, die
        Merge Requests in der Lieferung.
      */}

      {reiter === "uebersicht" ? <UebersichtReiter projektId={id} /> : null}

      {reiter === "puls" ? (
        <PulsReiter
          projektId={id}
          filter={{
            akteurArt: search.akteur as Akteursart | undefined,
            vorgang: search.vorgang as Vorgang | undefined,
            art: search.art as Objektart | undefined,
            seit: search.seit as "24h" | "7t" | "30t" | "alles" | undefined,
          }}
        />
      ) : null}

      {reiter === "aufgaben" ? (
        <AufgabenListe
          festesProjektId={id}
          rolle={rolle}
          neuOffen={neuOffen}
          onNeuOffen={setzeNeu}
          zeigeProjektFilter={false}
        />
      ) : null}

      {reiter === "meilensteine" ? (
        <MeilensteineReiter projektId={id} />
      ) : null}

      {reiter === "lieferung" ? (
        <LieferungReiter
          projektId={id}
          hatGitlab={Boolean(p.gitlabProjekt)}
          bereich={search.lieferung}
        />
      ) : null}

      {reiter === "durchsicht" ? (
        <DurchsichtReiter
          projektId={id}
          nurOffen={search.befund === "offen"}
        />
      ) : null}

      {reiter === "klaerungen" ? (
        <EntwurfOffen titel="Klärungen" />
      ) : null}

      {reiter === "seiten" ? (
        <Leerzustand
          titel="Seiten haben eine eigene Route."
          hinweis="Öffne die Seitenansicht des Projekts."
          aktionLabel="Zu den Seiten"
          onAktion={() => {
            globalThis.location.assign(`/projekte/${id}/seiten`);
          }}
        />
      ) : null}

      {reiter === "einstellungen" ? (
        <EinstellungenReiter projektId={id} rolle={rolle} />
      ) : null}

      {reiter === "personen" ? (
        (verzeichnis.data?.personen.length ?? 0) === 0 ? (
          <Leerzustand titel="Keine Personen." hinweis="Für dieses Projekt sind keine Rollen eingetragen." />
        ) : (
          <ul className="space-y-2" data-testid="projekt-personen">
            {verzeichnis.data!.personen.map((person) => (
              <li key={person.id} className="flex items-center justify-between rounded-md border border-gray-200 p-3 dark:border-gray-800">
                <span className="text-sm font-medium text-gray-900 dark:text-gray-50">{person.name}{person.id === ich.data?.personId ? " · Du" : ""}</span>
                <Badge variant="neutral">{person.rolle}</Badge>
              </li>
            ))}
          </ul>
        )
      ) : null}

      {reiter === "aufgaben" && kannAnlegen ? (
        <div className="fixed bottom-6 right-6 lg:right-10">
          <Button onClick={() => setzeNeu(true)} data-testid="aufgabe-neu-fab">
            Neue Aufgabe
          </Button>
        </div>
      ) : null}
    </>
  );
}
